import {
  P2GAdaptiveOverlay,
  P2GBox,
  P2GButton,
  P2GGrid,
  P2GIcon,
  P2GLink,
  P2GText,
  SvgAdd,
  SvgDuplicate
} from '@parcel2go/core-ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import P2GOptionContainer from '~/components/core/option/option';
import QuoteCardServiceLogo from '~/components/quote-results/card/quote-card-logo';
import useBasket from '~/hooks/use-basket';
import { TranslationNamespaces } from '~/lib/constants';
import { BasketItem, BasketMeta, BasketQuote, QuoteAddress, QuoteParcels } from '~/lib/types/basket-types';
import { BasketSections } from '~/lib/types/enums';
import { onlyUnique, SumNumber } from '~/lib/utils/array';

type addType = 'ADD' | 'DUPLICATE';

type optionProps = {
  onDuplicate: (id: string, incParcels: boolean) => void;
  onAdd: () => void;
  duplicatableItems: addAnotherType[];
  duplicatableServices: addAnotherType[];
  type: addType;
};

type props = {
  onDuplicate: (id: string, incParcels: boolean) => void;
  onAdd: () => void;
  isXs: boolean;
  type: addType;
};

type addAnotherType = {
  service: string;
  serviceId: string;
  id: string;
  itemId: string;
  itemDescription: string;
  count: number;
  origin: string;
  destination: string;
};

const Trim = (s: string, maxLength: number) => (s.length > maxLength ? `${s.substring(0, maxLength - 3)}...` : s);

const useAddAnotherOptions = () => {
  const { items } = useBasket();

  const basketItems = useMemo(() => {
    return Object.entries(items)
      .map(([name, item]) => ({
        name: name,
        item: item
      }))
      .filter((item) => (item.item.sections[BasketSections.META]?.payload as BasketMeta)?.completed === true);
  }, [items]);

  const generateFullKey = (item: BasketItem) => {
    const q = item?.sections[BasketSections.QUOTE]?.payload as BasketQuote;
    const qp = item?.sections[BasketSections.PARCELS]?.payload as QuoteParcels;
    const o = item?.sections[BasketSections.ORIGIN]?.payload as QuoteAddress;
    const d = item?.sections[BasketSections.DESTINATION]?.payload as QuoteAddress;
    return `${q.service.slug}-${qp.parcels.map((p) => p.contentSummary).join('-')}-${o.country}-${d.country}`;
  };

  const duplicatable: addAnotherType[] = useMemo(() => {
    const itemKeysBasedOnServiceAndDescription = basketItems
      .filter((x) => !!x.item?.sections)
      .map((item) => generateFullKey(item.item))
      .filter(onlyUnique);

    const groupCount = itemKeysBasedOnServiceAndDescription
      .map((item) => {
        const match = basketItems.find((x) => generateFullKey(x.item) === item);
        if (!match) return null;

        const service = match?.item.sections[BasketSections.QUOTE]?.payload as BasketQuote;
        return {
          service: service.service.name,
          serviceId: service.service.slug,
          id: item,
          itemId: match?.name || '',
          itemDescription: (match.item.sections[BasketSections.PARCELS]?.payload as QuoteParcels).parcels
            .map((p) => p.contentSummary)
            .join(', '),
          origin: (match.item.sections[BasketSections.ORIGIN]?.payload as QuoteAddress).country,
          destination: (match.item.sections[BasketSections.DESTINATION]?.payload as QuoteAddress).country,
          count: basketItems.filter((i) => generateFullKey(i.item) === item).length
        };
      })
      .filter((x): x is addAnotherType => x != null)
      .sort((a, b) => b.count - a.count);
    //   .slice(0, 1);
    return groupCount;
  }, [basketItems]);

  const duplicatableService: addAnotherType[] = useMemo(() => {
    const itemKeysBasedOnServiceAndDescription = duplicatable.map((item) => item.serviceId).filter(onlyUnique);
    const groupCount = itemKeysBasedOnServiceAndDescription
      .map((serviceId) => {
        const matches = duplicatable.filter((x) => x.serviceId === serviceId);
        if (!matches.length) return null;
        return {
          ...matches[0],
          count: SumNumber(matches.map((x) => x.count))
        };
      })
      .filter((x): x is addAnotherType => x != null)
      .sort((a, b) => b.count - a.count);
    return groupCount;
  }, [duplicatable]);

  return { duplicatable: duplicatable || [], duplicatableService: duplicatableService || [] };
};

export const AddAgainOptions: React.FC<optionProps> = ({
  onAdd,
  onDuplicate,
  duplicatableItems,
  duplicatableServices,
  type
}) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const max = 2;
  const [dupleLimit, setDupeLimit] = useState(max);
  const [serviceLimit, setServiceLimit] = useState(max);
  return (
    <P2GGrid container direction="column" gap={1}>
      {duplicatableItems.length > 0 && (
        <>
          <P2GGrid item>
            <P2GText variant="subtitle1" fontWeight="bold">
              {t('BASKET_ADD_ANOTHER_SENDAGAIN_TITLE', { defaultValue: 'Same parcel to a new recipient' })}
            </P2GText>
          </P2GGrid>
          {duplicatableItems.slice(0, dupleLimit).map((d) => (
            <P2GGrid item key={d.id}>
              <P2GOptionContainer variant="add-again" onClick={() => onDuplicate(d.itemId, true)}>
                <P2GGrid container wrap="nowrap" alignItems="center" gap={2}>
                  <P2GGrid item flexShrink={1}>
                    <QuoteCardServiceLogo width="80px" slug={d.serviceId} />
                    <P2GText
                      align="center"
                      component="p"
                      variant="caption"
                    >{`${d.origin} to ${d.destination}`}</P2GText>
                  </P2GGrid>
                  <P2GGrid item>
                    <P2GText fontWeight="bold" component="p">
                      <b>{Trim(d.itemDescription, 30)}</b>
                    </P2GText>
                    <P2GText variant="body2">{d.service}</P2GText>
                  </P2GGrid>
                </P2GGrid>
              </P2GOptionContainer>
            </P2GGrid>
          ))}
          {duplicatableItems.length > dupleLimit && (
            <P2GGrid item>
              <P2GBox align="center">
                <P2GLink onClick={() => setDupeLimit(duplicatableItems.length)}>
                  {t('BASKET_ADD_ANOTHER_VIEWALL_BUTTON', { defaultValue: 'View All' })}
                </P2GLink>
              </P2GBox>
            </P2GGrid>
          )}
        </>
      )}
      {type === 'ADD' && (
        <>
          {duplicatableServices.length > 0 && (
            <>
              <P2GGrid item mt={2}>
                <P2GText variant="subtitle1" fontWeight="bold">
                  {t('BASKET_ADD_ANOTHER_NEWPARCEL_TITLE', { defaultValue: 'New parcel using:' })}
                </P2GText>
              </P2GGrid>
              {duplicatableServices.slice(0, serviceLimit).map((d) => (
                <P2GGrid item key={d.id}>
                  <P2GOptionContainer variant="add-again" onClick={() => onDuplicate(d.itemId, false)}>
                    <P2GGrid container wrap="nowrap" alignItems="center" gap={2}>
                      <P2GGrid item flexShrink={1}>
                        <QuoteCardServiceLogo width="80px" slug={d.serviceId} />
                        <P2GText
                          align="center"
                          component="p"
                          variant="caption"
                        >{`${d.origin} to ${d.destination}`}</P2GText>
                      </P2GGrid>
                      <P2GGrid item>
                        <P2GText fontWeight="bold" component="p">
                          {d.service}
                        </P2GText>
                      </P2GGrid>
                    </P2GGrid>
                  </P2GOptionContainer>
                </P2GGrid>
              ))}
              {duplicatableServices.length > serviceLimit && (
                <P2GGrid item>
                  <P2GBox align="center">
                    <P2GLink onClick={() => setServiceLimit(duplicatableItems.length)}>
                      {t('BASKET_ADD_ANOTHER_VIEWALL_BUTTON', { defaultValue: 'View All' })}
                    </P2GLink>
                  </P2GBox>
                </P2GGrid>
              )}
            </>
          )}
          <P2GGrid item mt={2}>
            <P2GOptionContainer variant="add-again" onClick={onAdd}>
              <P2GBox pt={1} pb={1}>
                <P2GText>
                  <b>{t('BASKET_ADD_ANOTHER_GETQUOTE_BUTTON', { defaultValue: 'Get a New Quote' })}</b>
                </P2GText>
              </P2GBox>
            </P2GOptionContainer>
          </P2GGrid>
        </>
      )}
    </P2GGrid>
  );
};

const AddAnother: React.FC<props> = ({ onAdd, isXs, onDuplicate, type }) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { duplicatable, duplicatableService } = useAddAnotherOptions();
  const [open, setOpen] = useState(false);

  const onClick = duplicatable?.length || duplicatableService?.length ? () => setOpen(true) : onAdd;

  return (
    <>
      <P2GAdaptiveOverlay
        isOpen={open}
        onClose={() => setOpen(false)}
        closeButtonText="Cancel"
        modalTitle=""
        desktopViewOverride="modal"
      >
        <AddAgainOptions
          onAdd={onAdd}
          onDuplicate={onDuplicate}
          duplicatableItems={duplicatable}
          duplicatableServices={duplicatableService}
          type={type}
        />
      </P2GAdaptiveOverlay>
      <P2GButton
        variant="tertiary"
        size="large"
        onClick={onClick}
        fullWidth={isXs}
        startIcon={<P2GIcon size="sm">{type === 'ADD' ? <SvgAdd /> : <SvgDuplicate />}</P2GIcon>}
      >
        {t(`BASKET_${type}_PARCEL_BUTTON`, {
          defaultValue: type === 'ADD' ? 'Add Another Parcel' : 'Duplicate from Parcel'
        })}
      </P2GButton>
    </>
  );
};

export default AddAnother;
